/**
 * /auth/verify — magic-link landing page.
 *
 * Reads ?token=…, POSTs to /api/auth/email/verify, and routes:
 *   - 200 + onboarding_complete=false  → /onboarding
 *   - 200 + onboarding_complete=true   → /poc
 *   - 400 token_expired / token_invalid → calm error + "send a fresh link"
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, CheckCircle2, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { postEmailStart, postEmailVerify } from "@/lib/api";

export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [state, setState] = useState("verifying");  // verifying|success|expired|invalid|error
  const [retryEmail, setRetryEmail] = useState("");
  const [retrySent, setRetrySent] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("invalid");
      return undefined;
    }
    (async () => {
      const { ok, status, body } = await postEmailVerify(token);
      if (cancelled) return;
      if (ok) {
        setState("success");
        const next = body?.onboarding_complete ? "/poc" : "/onboarding";
        // Tiny delay so the success state is visible before redirect.
        setTimeout(() => navigate(next, { replace: true }), 500);
        return;
      }
      const err = body?.detail?.error || "";
      if (status === 400 && err === "token_expired") setState("expired");
      else if (status === 400) setState("invalid");
      else setState("error");
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const onResend = async (e) => {
    e?.preventDefault?.();
    if (!retryEmail.trim() || retrying) return;
    setRetrying(true);
    const { ok } = await postEmailStart(retryEmail.trim());
    setRetrying(false);
    if (ok) setRetrySent(true);
  };

  return (
    <div
      data-testid="verify-page"
      className="relative flex min-h-screen flex-col items-center justify-center bg-[hsl(var(--background))] px-4 py-10"
    >
      <div className="w-full max-w-md rounded-3xl border border-border/70 bg-[hsl(var(--card))] px-8 py-10 shadow-[0_22px_60px_-20px_rgba(59,74,107,0.18)]">
        {state === "verifying" && (
          <Status
            icon={<Loader2 className="h-5 w-5 animate-spin" />}
            heading="Opening the line…"
            body="One moment while we sign you in."
            tid="verifying"
          />
        )}
        {state === "success" && (
          <Status
            icon={<CheckCircle2 className="h-5 w-5 text-[hsl(var(--accent))]" />}
            heading="Welcome back."
            body="Taking you to the call now."
            tid="success"
          />
        )}
        {(state === "expired" || state === "invalid" || state === "error") && (
          <>
            <Status
              icon={<AlertCircle className="h-5 w-5 text-muted-foreground" />}
              heading={
                state === "expired"
                  ? "This link has expired."
                  : state === "invalid"
                  ? "This link has already been used or is no longer valid."
                  : "Something interrupted that."
              }
              body="Send a fresh link and we'll try again."
              tid={state}
            />

            {!retrySent ? (
              <form onSubmit={onResend} className="mt-7 flex flex-col gap-3">
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={retryEmail}
                    onChange={(e) => setRetryEmail(e.target.value)}
                    data-testid="verify-retry-email"
                    className="h-12 w-full rounded-full border border-border/70 bg-background pl-11 pr-4 text-[15px] outline-none transition focus:border-[hsl(var(--accent))]/60 focus:ring-2 focus:ring-[hsl(var(--accent))]/30"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={!retryEmail.trim() || retrying}
                  data-testid="verify-retry-submit"
                  className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/95 disabled:opacity-60"
                >
                  {retrying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : "Send a fresh link"}
                </Button>
              </form>
            ) : (
              <p
                data-testid="verify-retry-sent"
                className="mt-6 rounded-2xl border border-border/70 bg-background px-4 py-3 text-[13px] text-muted-foreground"
              >
                Sent. Check your email — and your spam folder if you don't see it.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Status({ icon, heading, body, tid }) {
  return (
    <div data-testid={`verify-status-${tid}`} className="flex flex-col items-center text-center">
      <div className="grid h-11 w-11 place-items-center rounded-full border border-border/70 bg-background">
        {icon}
      </div>
      <h1
        className="mt-5 text-2xl font-medium tracking-tight text-foreground"
        style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
      >
        {heading}
      </h1>
      <p className="mt-2 text-[14px] text-muted-foreground">{body}</p>
    </div>
  );
}
